import { useBoolean } from 'ahooks';
import { keyBy } from 'lodash';
import React, { FC, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ProjectId } from 'api/models';
import { Project, ProjectDetailTabKeys } from 'api/project';
import { Selectable } from 'components/atoms/grid';
import {
  HideForCustomer,
  HideForVendor,
} from 'components/atoms/user-type-utils';
import { ProjectTitle } from 'components/molecules/project-details/header/project-title';
import { ProjectDetailTabs } from 'components/molecules/project-details/header/tabs/tabs';
import { useKanbanGroupBy } from 'components/organisms/filter-menu-sidebar/use-group-by-hash';
import { TaskBoard } from 'components/organisms/kanban-board';
import {
  useColumnItems,
  useRowItems,
} from 'components/organisms/kanban-board/group-by';
import { TaskBoardGroupBy } from 'components/organisms/kanban-board/task-group-by';
import {
  SingleSelectField,
  TextField,
} from 'components/organisms/tasks/fields';
import { TaskFilterView } from 'components/organisms/tasks/filter-views';
import { useFilters } from 'components/organisms/tasks/use-filters';
import { ProjectTasksViewSwitcher } from 'components/pages/task-list/project-tasks-view-switcher';
import { useProjectTaskListFilter } from 'components/pages/task-list/use-project-task-list-filter';
import { Neon } from 'components/templates/neon';
import { RootState } from 'data';
import { useProjectPhaseByProjectId } from 'data/projects';
import { useCustomerPreview } from 'hooks/use-customer-preview';
import { useIsExternalUser } from 'hooks/use-user-type';
import { useProjectWebsocket } from 'hooks/websockets';
import { Empty } from 'stores/core';
import { FieldsStore, TaskField } from 'stores/fields';
import { getRandomColor } from 'utils/get-random-color';
import { SelectionControls } from '../selection-controls';
import { useProjectTasksRefresh } from '../use-project-tasks-refresh';
import { useProjectTasksSocketUpdates } from '../use-project-tasks-socket-updates';
import { useSelections } from '../use-selections';

const excludedTaskFields = [
  'taskId',
  'taskDescription',
  'isCsatEnabled',
  'project',
  'defaultTimeEntryBillable',
  'defaultTimeEntryCategory',
  'privateTaskDescription',
];

export const ProjectTaskBoardPage: FC<{
  project: Project;
}> = ({ project }) => {
  const { projectId } = project;
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const phases = useProjectPhaseByProjectId(projectId);
  const { isPreview } = useCustomerPreview();
  const isExternalUser = useIsExternalUser();

  const excludedFieldsForFilters = useMemo(
    () => [...excludedTaskFields, 'phaseName'],
    [],
  );
  const fields = useMemo<TaskField[]>(
    () =>
      FieldsStore.enabledTaskFields(fieldsStore).filter(
        ({ fieldName }) => !excludedTaskFields.includes(fieldName),
      ),
    [fieldsStore],
  );
  const fieldsForFilter = useMemo(
    () =>
      FieldsStore.enabledTaskFields(fieldsStore)
        .filter(
          ({ fieldName }) => !excludedFieldsForFilters.includes(fieldName),
        )
        .map((field) => {
          return field.fieldName === 'projectPhaseId'
            ? ({
                ...field,
                metaData: {
                  ...field.metaData,
                  options: phases.map((phase) => ({
                    active: true,
                    label: phase?.projectPhaseName,
                    value: phase?.projectPhaseId,
                    color: getRandomColor(phase?.projectPhaseName || ''),
                  })),
                },
              } as TaskField)
            : field;
        }),
    [excludedFieldsForFilters, fieldsStore, phases],
  );
  const fields_loading = !FieldsStore.loaded(fieldsStore);
  const fields_lookup_by_name = useMemo(
    () =>
      keyBy(
        fieldsForFilter.filter(
          ({ fieldName }) => !excludedFieldsForFilters.includes(fieldName),
        ),
        'fieldName',
      ) as Record<string, TaskField>,
    [excludedFieldsForFilters, fieldsForFilter],
  );
  const { byProjectId } = useSelector(({ tasks_v2 }: RootState) => tasks_v2);
  const taskIds = useMemo(
    () => byProjectId[projectId] ?? Empty(),
    [byProjectId, projectId],
  );
  const status = taskIds[0];
  const { filter, filterFunction, clearFilters, selectFilter, replaceFilter } =
    useFilters(
      fields_lookup_by_name,
      status === 'refreshing' ? 'loaded' : status,
    );
  const [filterInitialized, setFilterInitialized] = useState(false);
  const projectField = FieldsStore.getTaskFieldByName('project')(
    fieldsStore,
  )[1] as (SingleSelectField & TaskField) | undefined;
  const taskNameField = FieldsStore.getTaskFieldByName('taskName')(
    fieldsStore,
  )[1] as (TextField & TaskField) | undefined;

  useProjectTaskListFilter({
    localStorageKey: `task_list.query.project_id-${projectId}`,
    taskStatus: status,
    replaceFilter,
    filter,
    fieldsLoading: fields_loading,
    fieldLookup: fields_lookup_by_name,
    filterInitialized,
    setFilterInitialized,
  });

  useProjectTasksRefresh({ projectId: ProjectId(projectId) });
  useProjectTasksSocketUpdates(ProjectId(projectId));
  useProjectWebsocket(ProjectId(projectId));

  const [groupBy, setGroupBy] = useKanbanGroupBy(
    ProjectId(projectId),
    fields_lookup_by_name,
  );
  const rowFields = useRowItems(fields);
  const columnFields = useColumnItems(fields);
  const [isGroupChangeLoading, { setTrue, setFalse }] = useBoolean(false);

  const setGroupChangeLoading = (value: boolean) => {
    return value ? setTrue() : setFalse();
  };
  const selectableRef = useRef<Selectable>(null);
  const { reset, selectAll, count } = useSelections(selectableRef);

  return (
    <Neon
      title={<ProjectTitle project={project} />}
      tabs={
        <HideForCustomer>
          <ProjectDetailTabs
            projectId={ProjectId(projectId)}
            activeTabKey={ProjectDetailTabKeys.TaskBoard}
          />
        </HideForCustomer>
      }
      actions={
        count > 0
          ? {
              primary: (
                <SelectionControls
                  count={count}
                  reset={reset}
                  selectAll={selectAll}
                />
              ),
            }
          : {
              primary: (
                <TaskFilterView
                  isFieldsLoading={fields_loading}
                  filter={filter}
                  fields={fieldsForFilter}
                  selectFilter={selectFilter}
                  clearFilters={clearFilters}
                  lookup={fields_lookup_by_name}
                  includeSavedViews={!!(isPreview || isExternalUser)}
                />
              ),
              secondary: (
                <>
                  <TaskBoardGroupBy
                    isLoading={isGroupChangeLoading}
                    groupBy={groupBy}
                    setGroupBy={setGroupBy}
                    columnFields={columnFields}
                    rowFields={rowFields}
                    setGroupChangeLoading={setGroupChangeLoading}
                  />
                </>
              ),
              central: (
                <HideForVendor>
                  <ProjectTasksViewSwitcher projectId={ProjectId(projectId)} />
                </HideForVendor>
              ),
            }
      }
      content={
        <TaskBoard
          projectId={ProjectId(project.projectId)}
          initialized={filterInitialized}
          filterFunction={filterFunction}
          groupBy={groupBy}
          projectField={projectField}
          taskNameField={taskNameField}
          isLoading={isGroupChangeLoading}
        />
      }
    />
  );
};
